import React, { useState, useEffect } from 'react';
import { Form, Message, Segment, Header, Divider, Menu } from 'semantic-ui-react';
import BaseModal, { BaseModalAction } from '../../../components/shared/BaseModal';
import { useToast } from '../../../hooks/useToast';
import campaignService from '../../../services/campaigns/campaignService';
import {
  CampaignResponseDto,
  UpdateBonificationRulesDto,
  UpdateCampaignEligibilityRulesDto,
  CommissionTiersDocument,
} from '../../../types/campaigns.types';
import {
  DEFAULT_COMMISSION_TIERS,
  DEFAULT_SCORING_TABLE,
  cloneScoringTable,
  defaultEligibilityRulesForm,
  mapEligibilityRulesToForm,
  defaultTop1ExtraRules,
  defaultSemanalDocument,
} from '../../../constants/campaignScoringDefaults';
import CommissionTiersEditor, { normalizeCommissionTiers } from './CommissionTiersEditor';
import ScoringTableEditor from './ScoringTableEditor';
import EligibilityRulesForm from './EligibilityRulesForm';
import ExtraCampaignTop1Tab from './ExtraCampaignTop1Tab';
import ExtraCampaignSemanalTab from './ExtraCampaignSemanalTab';
import { normalizeWeeklyWindowsDocument } from './WeeklyWindowsEditor';
import GlassmorphismTheme from '../../../styles/glassmorphism';

interface BonificationRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: CampaignResponseDto;
  onSuccess: () => void;
}

type RulesTab = 'pontuacao' | 'elegibilidade' | 'tiers' | 'top1' | 'semanal' | 'bonificacoes';

const emptyForm = (campaign: CampaignResponseDto): UpdateBonificationRulesDto => ({
  valorMinimoLinha: undefined,
  ativaHorarioComercial: false,
  horarioInicio: undefined,
  horarioFim: undefined,
  diaSemanaInicio: undefined,
  diaSemanaFim: undefined,
  bonificacaoSextaAtiva: false,
  valorBonificacaoSexta: undefined,
  bonificacaoPorValorAtiva: false,
  valorMinimoBonificacao: undefined,
  valorBonificacaoPorValor: undefined,
  validarEnderecoEntrega: false,
  permitirExtornoEntrega: false,
  usarSistemaPontos: true,
  usarRegrasPrimeiroMes: true,
  commissionTiers: cloneTiers(DEFAULT_COMMISSION_TIERS),
  scoringTable: cloneScoringTable(DEFAULT_SCORING_TABLE),
  campanhaTop1Ativa: true,
  valorPremiacaoTop1: undefined,
  top1ExtraRules: defaultTop1ExtraRules(),
  campanhaSemanalAtiva: true,
  campanhasSemanais: defaultSemanalDocument(campaign),
});

const cloneTiers = (tiers: CommissionTiersDocument): CommissionTiersDocument =>
  normalizeCommissionTiers({
    segundoMes: tiers.segundoMes.map((t) => ({ ...t })),
    primeiroMes: tiers.primeiroMes.map((t) => ({ ...t })),
  });

const BonificationRulesModal: React.FC<BonificationRulesModalProps> = ({
  isOpen,
  onClose,
  campaign,
  onSuccess,
}) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<RulesTab>('pontuacao');
  const [formData, setFormData] = useState<UpdateBonificationRulesDto>(() => emptyForm(campaign));
  const [eligibilityData, setEligibilityData] = useState<UpdateCampaignEligibilityRulesDto>(
    defaultEligibilityRulesForm()
  );
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const campaignKind = campaign.kind ?? 0;
  const isPontuacaoCampaign = campaignKind === 1 || campaignKind === 2;
  const hasMeta = campaignKind === 0 || campaignKind === 2;
  const hasPontos = campaignKind === 1 || campaignKind === 2;

  useEffect(() => {
    if (campaign.bonificationRules) {
      const rules = campaign.bonificationRules;
      setFormData({
        valorMinimoLinha: rules.valorMinimoLinha,
        ativaHorarioComercial: rules.ativaHorarioComercial,
        horarioInicio: rules.horarioInicio,
        horarioFim: rules.horarioFim,
        diaSemanaInicio: rules.diaSemanaInicio,
        diaSemanaFim: rules.diaSemanaFim,
        bonificacaoSextaAtiva: rules.bonificacaoSextaAtiva,
        valorBonificacaoSexta: rules.valorBonificacaoSexta,
        bonificacaoPorValorAtiva: rules.bonificacaoPorValorAtiva,
        valorMinimoBonificacao: rules.valorMinimoBonificacao,
        valorBonificacaoPorValor: rules.valorBonificacaoPorValor,
        validarEnderecoEntrega: rules.validarEnderecoEntrega,
        permitirExtornoEntrega: rules.permitirExtornoEntrega,
        usarSistemaPontos: rules.usarSistemaPontos ?? true,
        usarRegrasPrimeiroMes: rules.usarRegrasPrimeiroMes ?? true,
        commissionTiers: rules.commissionTiers
          ? cloneTiers(rules.commissionTiers)
          : cloneTiers(DEFAULT_COMMISSION_TIERS),
        scoringTable: rules.scoringTable
          ? cloneScoringTable(rules.scoringTable)
          : cloneScoringTable(DEFAULT_SCORING_TABLE),
        campanhaTop1Ativa: rules.campanhaTop1Ativa ?? false,
        valorPremiacaoTop1: rules.valorPremiacaoTop1,
        top1ExtraRules: rules.top1ExtraRules ?? {
          ...defaultTop1ExtraRules(),
          valorPremiacao: rules.valorPremiacaoTop1,
        },
        campanhaSemanalAtiva: rules.campanhaSemanalAtiva ?? false,
        campanhasSemanais: normalizeWeeklyWindowsDocument(
          rules.campanhasSemanais ?? defaultSemanalDocument(campaign)
        ),
      });
    } else {
      setFormData(emptyForm(campaign));
    }

    if (isOpen) {
      setLoadingEligibility(true);
      campaignService
        .getEligibilityRules(campaign.id)
        .then((rules) => {
          setEligibilityData(mapEligibilityRulesToForm(rules));
          setRecalculating(!!rules.recalculando);
        })
        .catch(() => {
          setEligibilityData(defaultEligibilityRulesForm());
        })
        .finally(() => setLoadingEligibility(false));
    }
    setActiveTab(hasPontos ? 'pontuacao' : 'elegibilidade');
    setErrors({});
  }, [campaign, isOpen, hasPontos]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.ativaHorarioComercial) {
      if (!formData.horarioInicio || !formData.horarioFim) {
        newErrors.horarioComercial = 'Horários de início e fim são obrigatórios quando horário comercial está ativo';
      }
      if (formData.diaSemanaInicio === undefined || formData.diaSemanaFim === undefined) {
        newErrors.diaSemana = 'Dias da semana são obrigatórios quando horário comercial está ativo';
      }
    }

    if (formData.bonificacaoSextaAtiva && (!formData.valorBonificacaoSexta || formData.valorBonificacaoSexta <= 0)) {
      newErrors.bonificacaoSexta = 'Valor da bonificação de sexta-feira é obrigatório quando ativada';
    }

    if (formData.bonificacaoPorValorAtiva) {
      if (!formData.valorMinimoBonificacao || formData.valorMinimoBonificacao <= 0) {
        newErrors.valorMinimoBonificacao = 'Valor mínimo é obrigatório quando bonificação por valor está ativa';
      }
      if (!formData.valorBonificacaoPorValor || formData.valorBonificacaoPorValor <= 0) {
        newErrors.valorBonificacaoPorValor = 'Valor da bonificação é obrigatório quando bonificação por valor está ativa';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (activeTab === 'elegibilidade') {
      const metaStatuses = eligibilityData.meta?.allowedContractStatuses ?? [];
      const pontosStatuses = eligibilityData.pontos?.allowedContractStatuses ?? [];
      if (hasMeta && !metaStatuses.length) {
        showError('Selecione ao menos um status de contrato no bloco Metas');
        return;
      }
      if (hasPontos && !pontosStatuses.length) {
        showError('Selecione ao menos um status de contrato no bloco Pontuação');
        return;
      }
      try {
        setLoading(true);
        const result = await campaignService.updateEligibilityRules(campaign.id, eligibilityData);
        showSuccess(result.recalculationEnqueued
          ? 'Elegibilidade salva. Recálculo em andamento…'
          : 'Elegibilidade salva com sucesso');
        if (result.recalculationEnqueued) setRecalculating(true);
        onSuccess();
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        showError(err.response?.data?.message || 'Erro ao salvar elegibilidade');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload: UpdateBonificationRulesDto = {
        ...formData,
        commissionTiers: formData.commissionTiers
          ? normalizeCommissionTiers(formData.commissionTiers)
          : formData.commissionTiers,
        campanhasSemanais: formData.campanhasSemanais
          ? normalizeWeeklyWindowsDocument(formData.campanhasSemanais)
          : formData.campanhasSemanais,
        usarSistemaPontos: hasPontos ? true : formData.usarSistemaPontos,
        top1ExtraRules: formData.campanhaTop1Ativa ? formData.top1ExtraRules : undefined,
        scoringTable: formData.scoringTable
          ? {
              ...formData.scoringTable,
              renegociacaoAptaPontos:
                formData.scoringTable.faixasPlano.find((f) =>
                  (f.tiposLinha ?? (f.tipoLinha ? [f.tipoLinha] : [])).includes('renegociacaoApta')
                )?.pontos ?? formData.scoringTable.renegociacaoAptaPontos,
            }
          : formData.scoringTable,
      };
      await campaignService.updateBonificationRules(campaign.id, payload);
      showSuccess('Regras atualizadas com sucesso');
      onSuccess();
      onClose();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar regras');
    } finally {
      setLoading(false);
    }
  };

  const modalActions: BaseModalAction[] = [
    { key: 'cancel', content: 'Cancelar', icon: 'cancel', onClick: onClose },
    {
      key: 'save',
      content: activeTab === 'elegibilidade' ? 'Salvar elegibilidade' : 'Salvar Regras',
      icon: 'save',
      primary: true,
      onClick: handleSubmit,
      loading,
      disabled: activeTab === 'elegibilidade' && (loadingEligibility || recalculating),
    },
  ];

  const dayOptions = [
    { key: 0, text: 'Domingo', value: 0 },
    { key: 1, text: 'Segunda-feira', value: 1 },
    { key: 2, text: 'Terça-feira', value: 2 },
    { key: 3, text: 'Quarta-feira', value: 3 },
    { key: 4, text: 'Quinta-feira', value: 4 },
    { key: 5, text: 'Sexta-feira', value: 5 },
    { key: 6, text: 'Sábado', value: 6 },
  ];

  const timeSpanToTime = (timeSpan?: string): string => {
    if (!timeSpan) return '';
    const parts = timeSpan.split(':');
    return `${parts[0]}:${parts[1]}`;
  };

  const timeToTimeSpan = (time: string): string => (time ? `${time}:00` : '');

  const renderPontuacaoTab = () => (
    <>
      {!isPontuacaoCampaign && (
        <Message info>
          Estas configurações aplicam-se a campanhas com bloco de <strong>pontuação/comissão</strong>.
        </Message>
      )}

      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem', marginBottom: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>Regras de comissão</Header>
        <Form.Field>
          <Form.Checkbox
            label="Aplicar regras de primeiro mês (tiers reduzidos)"
            checked={formData.usarRegrasPrimeiroMes ?? true}
            onChange={(_, { checked }) => setFormData({ ...formData, usarRegrasPrimeiroMes: checked as boolean })}
          />
        </Form.Field>
      </Segment>

      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>Tabela de pontos por tipo de linha</Header>
        {formData.scoringTable && (
          <ScoringTableEditor
            table={formData.scoringTable}
            onChange={(scoringTable) => setFormData({ ...formData, scoringTable })}
          />
        )}
      </Segment>
    </>
  );

  const renderElegibilidadeTab = () => (
    <>
      {recalculating && (
        <Message warning icon="sync" content="Recálculo da campanha em andamento. Aguarde…" />
      )}
      {loadingEligibility ? (
        <Message info content="Carregando regras de elegibilidade…" />
      ) : (
        <EligibilityRulesForm
          formData={eligibilityData}
          onChange={setEligibilityData}
          campaignKind={campaignKind}
        />
      )}
    </>
  );

  const renderTiersTab = () => (
    <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem' }}>
      <Header as="h4" style={{ color: '#2c3e50' }}>Faixas de comissão (tiers)</Header>
      {formData.commissionTiers && (
        <CommissionTiersEditor
          tiers={formData.commissionTiers}
          onChange={(commissionTiers) => setFormData({ ...formData, commissionTiers })}
        />
      )}
    </Segment>
  );

  const renderTop1Tab = () => (
    <ExtraCampaignTop1Tab formData={formData} onChange={setFormData} />
  );

  const renderSemanalTab = () => (
    <ExtraCampaignSemanalTab campaign={campaign} formData={formData} onChange={setFormData} />
  );

  /* Aba Bonificações desativada temporariamente
  const renderBonificacoesTab = () => (
    <>
      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem', marginBottom: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50', marginBottom: '1rem' }}>Valor Mínimo da Linha</Header>
        <Form.Field>
          <label htmlFor="valor-minimo-linha" style={{ color: '#2c3e50' }}>Valor Mínimo (R$)</label>
          <Form.Input
            id="valor-minimo-linha"
            type="number"
            step="0.01"
            min="0"
            value={formData.valorMinimoLinha || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                valorMinimoLinha: e.target.value ? parseFloat(e.target.value) : undefined,
              })
            }
            placeholder="0.00"
            style={GlassmorphismTheme.components.inputGlass}
          />
        </Form.Field>
      </Segment>

      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem', marginBottom: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50', marginBottom: '1rem' }}>Horário Comercial</Header>
        <Form.Field>
          <Form.Checkbox
            id="ativa-horario-comercial"
            label="Ativar Horário Comercial"
            checked={formData.ativaHorarioComercial}
            onChange={(_, { checked }) =>
              setFormData({ ...formData, ativaHorarioComercial: checked as boolean })
            }
          />
        </Form.Field>
        {formData.ativaHorarioComercial && (
          <>
            {errors.horarioComercial && <Message error size="small">{errors.horarioComercial}</Message>}
            {errors.diaSemana && <Message error size="small">{errors.diaSemana}</Message>}
            <Form.Group widths="equal">
              <Form.Field>
                <label style={{ color: '#2c3e50' }}>Dia (Início)</label>
                <Form.Select
                  options={dayOptions}
                  value={formData.diaSemanaInicio}
                  onChange={(_, { value }) => setFormData({ ...formData, diaSemanaInicio: value as number })}
                  style={GlassmorphismTheme.components.inputGlass}
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: '#2c3e50' }}>Dia (Fim)</label>
                <Form.Select
                  options={dayOptions}
                  value={formData.diaSemanaFim}
                  onChange={(_, { value }) => setFormData({ ...formData, diaSemanaFim: value as number })}
                  style={GlassmorphismTheme.components.inputGlass}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field>
                <label style={{ color: '#2c3e50' }}>Horário Início</label>
                <Form.Input
                  type="time"
                  value={timeSpanToTime(formData.horarioInicio)}
                  onChange={(e) => setFormData({ ...formData, horarioInicio: timeToTimeSpan(e.target.value) })}
                  style={GlassmorphismTheme.components.inputGlass}
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: '#2c3e50' }}>Horário Fim</label>
                <Form.Input
                  type="time"
                  value={timeSpanToTime(formData.horarioFim)}
                  onChange={(e) => setFormData({ ...formData, horarioFim: timeToTimeSpan(e.target.value) })}
                  style={GlassmorphismTheme.components.inputGlass}
                />
              </Form.Field>
            </Form.Group>
          </>
        )}
      </Segment>

      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem', marginBottom: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>Bonificação Sexta-feira</Header>
        <Form.Field>
          <Form.Checkbox
            label="Ativar Bonificação de Sexta-feira"
            checked={formData.bonificacaoSextaAtiva}
            onChange={(_, { checked }) => setFormData({ ...formData, bonificacaoSextaAtiva: checked as boolean })}
          />
        </Form.Field>
        {formData.bonificacaoSextaAtiva && (
          <Form.Field error={!!errors.bonificacaoSexta}>
            <label style={{ color: '#2c3e50' }}>Valor (R$)</label>
            <Form.Input
              type="number"
              step="0.01"
              value={formData.valorBonificacaoSexta || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  valorBonificacaoSexta: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              style={GlassmorphismTheme.components.inputGlass}
            />
          </Form.Field>
        )}
      </Segment>

      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem', marginBottom: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>Bonificação por Valor</Header>
        <Form.Field>
          <Form.Checkbox
            label="Ativar Bonificação por Valor"
            checked={formData.bonificacaoPorValorAtiva}
            onChange={(_, { checked }) => setFormData({ ...formData, bonificacaoPorValorAtiva: checked as boolean })}
          />
        </Form.Field>
        {formData.bonificacaoPorValorAtiva && (
          <Form.Group widths="equal">
            <Form.Field error={!!errors.valorMinimoBonificacao}>
              <label style={{ color: '#2c3e50' }}>Valor Mínimo (R$)</label>
              <Form.Input
                type="number"
                step="0.01"
                value={formData.valorMinimoBonificacao || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    valorMinimoBonificacao: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                style={GlassmorphismTheme.components.inputGlass}
              />
            </Form.Field>
            <Form.Field error={!!errors.valorBonificacaoPorValor}>
              <label style={{ color: '#2c3e50' }}>Bonificação (R$)</label>
              <Form.Input
                type="number"
                step="0.01"
                value={formData.valorBonificacaoPorValor || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    valorBonificacaoPorValor: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                style={GlassmorphismTheme.components.inputGlass}
              />
            </Form.Field>
          </Form.Group>
        )}
      </Segment>

      <Segment style={{ ...GlassmorphismTheme.components.glassCard, padding: '1rem' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>Validação de Endereço</Header>
        <Form.Field>
          <Form.Checkbox
            label="Validar Endereço de Entrega"
            checked={formData.validarEnderecoEntrega}
            onChange={(_, { checked }) => setFormData({ ...formData, validarEnderecoEntrega: checked as boolean })}
          />
        </Form.Field>
        <Form.Field>
          <Form.Checkbox
            label="Permitir Extorno de Entrega"
            checked={formData.permitirExtornoEntrega}
            onChange={(_, { checked }) => setFormData({ ...formData, permitirExtornoEntrega: checked as boolean })}
          />
        </Form.Field>
      </Segment>
    </>
  );
  */

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Configuração — ${campaign.nome}`}
      icon="settings"
      size="large"
      actions={modalActions}
    >
      <Form>
        {Object.keys(errors).length > 0 && (
          <Message error>
            <Message.Header>Erros no formulário</Message.Header>
            <Message.List>
              {Object.values(errors).map((error, index) => (
                <Message.Item key={index}>{error}</Message.Item>
              ))}
            </Message.List>
          </Message>
        )}

        <Menu pointing secondary style={{ marginBottom: '1rem' }}>
          <Menu.Item
            name="Pontuação"
            active={activeTab === 'pontuacao'}
            onClick={() => setActiveTab('pontuacao')}
          />
          <Menu.Item
            name="Elegibilidade"
            active={activeTab === 'elegibilidade'}
            onClick={() => setActiveTab('elegibilidade')}
          />
          <Menu.Item name="Tiers" active={activeTab === 'tiers'} onClick={() => setActiveTab('tiers')} />
          <Menu.Item name="TOP 1" active={activeTab === 'top1'} onClick={() => setActiveTab('top1')} />
          <Menu.Item name="Semanal" active={activeTab === 'semanal'} onClick={() => setActiveTab('semanal')} />
          {/* Aba Bonificações desativada temporariamente
          <Menu.Item
            name="Bonificações"
            active={activeTab === 'bonificacoes'}
            onClick={() => setActiveTab('bonificacoes')}
          />
          */}
        </Menu>

        {activeTab === 'pontuacao' && renderPontuacaoTab()}
        {activeTab === 'elegibilidade' && renderElegibilidadeTab()}
        {activeTab === 'tiers' && renderTiersTab()}
        {activeTab === 'top1' && renderTop1Tab()}
        {activeTab === 'semanal' && renderSemanalTab()}
        {/* {activeTab === 'bonificacoes' && renderBonificacoesTab()} */}
      </Form>
    </BaseModal>
  );
};

export default BonificationRulesModal;
